import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useFlags } from 'launchdarkly-react-client-sdk'
import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import { CACHE_TAG_WORK_ORDER_DETAILS } from 'api/cacheTagTypes'
import { useUpdateWorkOrderMutation } from 'api/work-order/updateWorkOrder'
import { ReactComponent as MoneyCheckDollarPenIcon } from 'assets/MoneyCheckDollarPen.svg'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import TooltipMenuItem from 'components/common/TooltipMenuItem'
import { getWorkOrderListInvalidatesTags } from 'components/customer-details/content/work-orders/utils'
import { isWORedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import T from 'T'
import { handleError } from 'utils/error'

const RemovePurchaseOrderMenuOption = ({
  workOrderId,
  serviceDate = '',
  configuredServiceId = '',
  existingPurchaseOrder = '',
  isLocked = false,
  onClose,
  onSuccess = noop,
}) => {
  const dispatch = useDispatch()
  const [updateWorkOrder, { isLoading }] = useUpdateWorkOrderMutation()
  const [isOpenRemovePurchaseOrderDialog, setIsOpenRemovePurchaseOrderDialog] = useState(false)

  const flags = useFlags()
  const isWORedesignEnabled = isWORedesignFeatureAvailable({ flags })

  const handleRemovePOClick = useCallback(() => setIsOpenRemovePurchaseOrderDialog(true), [])

  const handleRemovePOClose = useCallback(() => {
    setIsOpenRemovePurchaseOrderDialog(false)
    onClose()
  }, [onClose])

  const handleRemovePO = useCallback(() => {
    updateWorkOrder({ id: workOrderId, purchaseOrderNumber: '' })
      .unwrap()
      .then(() => {
        onClose()
        setIsOpenRemovePurchaseOrderDialog(false)
        toast.success(T.REMOVE_PURCHASE_ORDER_SUCCESS_MSG)
        const woInvalidateTags = getWorkOrderListInvalidatesTags({ isNew: isWORedesignEnabled, serviceDate, csId: configuredServiceId })
        dispatch(api.util.invalidateTags([CACHE_TAG_WORK_ORDER_DETAILS, ...woInvalidateTags]))
        onSuccess()
      })
      .catch(handleError)
  }, [updateWorkOrder, workOrderId, onClose, isWORedesignEnabled, serviceDate, configuredServiceId, dispatch, onSuccess])

  return (
    <>
      {existingPurchaseOrder ? (
        <TooltipMenuItem disabled={isLocked} sx={{ color: 'warning.main' }} tooltip={T.BILLING_LOCKED} onClick={handleRemovePOClick}>
          <ListItemIcon>
            <MoneyCheckDollarPenIcon height={24} width={24} />
          </ListItemIcon>
          <ListItemText>{T.REMOVE_PURCHASE_ORDER}</ListItemText>
        </TooltipMenuItem>
      ) : null}
      <HHConfirmDialog
        ConfirmButtonProps={{ loading: isLoading, color: 'warning' }}
        confirmButtonTitle={T.REMOVE}
        confirmDescription={T.REMOVE_PURCHASE_ORDER_CONFIRM_MSG}
        confirmTitle={T.REMOVE_PURCHASE_ORDER}
        isOpen={isOpenRemovePurchaseOrderDialog}
        onClose={handleRemovePOClose}
        onConfirm={handleRemovePO}
      />
    </>
  )
}

RemovePurchaseOrderMenuOption.propTypes = {
  workOrderId: PropTypes.string.isRequired,
  serviceDate: PropTypes.string,
  configuredServiceId: PropTypes.string,
  existingPurchaseOrder: PropTypes.string,
  isLocked: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

export default RemovePurchaseOrderMenuOption
